import { Map, fromJS, List } from 'immutable';
import Rx from 'rxjs/Rx';
import CuesheetRowActions from 'flux/actions/cuesheet/CuesheetRowActions';
import CuesheetTrackingActions from 'flux/actions/cuesheet/CuesheetTrackingActions';
import AlertActions from 'flux/actions/AlertActions';
import CuesheetCueClockActions from 'flux/actions/cuesheet/CuesheetCueClockActions';
import CuesheetTransientCellActions from 'flux/actions/cuesheet/CuesheetTransientCellActions';
import Messages from 'shared/constants/Messages';
import UIRegions from 'shared/constants/UIRegions';
import {
  RowTypes,
  PasteModes,
  EditableCellTypes,
  ValidCellFormatKeys,
} from './CuesheetConstants';
import CuesheetDataHelper from './CuesheetDataHelper';
import ItemSetData from 'shared/data/ItemSet';
import ItemSetListData from 'shared/data/ItemSetList';
import _ from 'underscore';
import { isGlobalElementManager } from 'main/components/show/cuesheet/cuesheetViewHelper';
import ToastActions from 'flux/actions/ToastActions';
import ConfirmActions from 'flux/actions/ConfirmActions';
import PublishGlobalElementsActions from 'flux/actions/PublishGlobalElementsActions';
import NewGlobalElementModalActions from 'flux/actions/NewGlobalElementModalActions';
import CuesheetCellActions from 'flux/actions/cuesheet/CuesheetCellActions';
import { persistCopyState } from 'flux/stores/copyPersistence';

const debug = require('debug')('shoflo:flux:CuesheetRowMixin.js');

/**
 * How long a single moveRows request may stay unsettled before we presume it
 * dead. Past this, it stops counting as a pending move (so remote row orders
 * are no longer suppressed) and stops blocking the request chain (so later
 * moves can still reach the server).
 */
const PENDING_MOVE_TTL_MS = 15000;

/**
 * CuesheetStore Row mixin
 *
 */
export default {
  init() {
    /**
     * Set up observable subjects
     */
    this.rowObservables = {};
    this.rowObservables.moveRowsDebounce = new Rx.Subject();
    this.rowObservables.addItemRow = new Rx.Subject();
    this.rowObservables.globalHighlightDebounce = new Rx.Subject();
    this.rowObservables.personalHighlightDebounce = new Rx.Subject();

    /**
     * Row move persistence lifecycle — one piece of pending state per phase:
     *
     *   gesture ──► BUFFERED ──(500ms quiet: flush)──► IN FLIGHT ──► SETTLED
     *               _hasBufferedRowMoves = true        push() onto   removed by
     *               (cleared only by the flush)        _inFlight-    identity in
     *                                                  MoveContexts  _onMoveRowsSettled
     *
     * _hasBufferedRowMoves is set on each gesture and cleared ONLY by the
     * debounced flush (flushBufferedRowMoves), which is also what persists the
     * buffered deltas. It is deliberately never cleared on a timer: doing so
     * would end pending protection while the Rx buffer still holds un-persisted
     * deltas, letting a realtime order overwrite the optimistic order before
     * those moves reach the API. The flush always fires after the last gesture
     * (only delayed, e.g. in a throttled background tab), so the flag cannot
     * stick — when it fires, protection hands off to the in-flight phase.
     *
     * Each _inFlightMoveContexts entry is the {showId, itemSetId,
     * dispatchedAt} a request was dispatched for. dispatchedAt is null while
     * the request is still queued behind an earlier one and is stamped only
     * when the request actually leaves, so the unsettled-request TTL never
     * expires a move that hasn't been sent yet. The request's settle callback
     * closes over its own entry and removes exactly that entry, so a
     * settlement can never be attributed to the wrong request, no matter how
     * late or out of order it arrives.
     *
     * _hasPendingRowMoves() is the single consumer of this state: while
     * anything is buffered or in flight, incoming realtime row orders
     * predate the local optimistic order and must not be applied (see
     * CuesheetRealTimeMixin._updateRowOrder), and our own responses wait
     * for the burst's last settlement before re-syncing (_onMoveRowsSettled).
     *
     * Requests are chained through _moveRowsRequestChain so only one is ever
     * in flight: the moveRows endpoint applies each delta as a non-atomic
     * read-modify-write of rowOrder, so overlapping requests would clobber
     * each other and silently drop a move. All contexts are still tracked
     * eagerly, so a queued-but-not-yet-sent move counts as pending. Each chain
     * link is bounded by PENDING_MOVE_TTL_MS so a request that never settles
     * can't block later moves from reaching the server forever.
     */
    this._hasBufferedRowMoves = false;
    this._inFlightMoveContexts = [];
    this._moveRowsRequestChain = Promise.resolve();

    /**
     * Subscriber for adding a row persistent (to db)
     */
    const addItemRowsPersistent = (requests) => {
      const geKeyId = '_GLOBAL_ELEMENTS';

      /**
       * Group the requests by 'afterRowId'
       */
      const groups = {};
      requests.forEach(({ afterRowId, items, globalElement }) => {
        const afterRowIdKey = globalElement
          ? `${afterRowId}${geKeyId}`
          : afterRowId;

        if (!groups[afterRowIdKey]) groups[afterRowIdKey] = [];
        groups[afterRowIdKey] = groups[afterRowIdKey].concat(items);
      });

      /**
       * Create a new observable
       * @type {[type]}
       */
      Rx.Observable.from(
        _.map(groups, (items, afterRowIdKey) => ({ afterRowIdKey, items })),
      )
        .delay(500)
        .subscribe(({ afterRowIdKey, items }) => {
          debug(
            'Persisting group of new rows after rowId ' + afterRowIdKey,
            items,
          );

          const isGlobalElement = afterRowIdKey.indexOf(geKeyId) !== -1;
          const afterRowId = afterRowIdKey.replace(geKeyId, '');

          const itemSetId = this.findFirstItemSetId();
          const state = this.getState();

          CuesheetRowActions.addItemRowsPersistent(
            state.get('teamId'),
            state.get('eventId'),
            state.get('showId'),
            state.get('itemSetListId'),
            itemSetId,
            afterRowId,
            items,
            isGlobalElement,
          );
        });
    };

    /**
     * Subscriber function for observables...move rows transient
     *
     * @param  {[type]} rowIds     [description]
     * @param  {[type]} afterRowId [description]
     * @return [type]              [description]
     */
    const moveRowsTransient = ({ rowIds, afterRowId }) => {
      debug('Moving rows transient', rowIds);

      this._hasBufferedRowMoves = true;

      const itemSetId = this.findFirstItemSetId();

      const next = this.moveRowsTransient(itemSetId, rowIds, afterRowId);

      this.setStateAndTrigger(
        this.selectLastCellSelected(this.recalculateTimeFields(next)),
      );
    };

    /**
     * Subscriber function for observables...flush the buffered move deltas
     * to the API once the user has stopped moving rows
     */
    const flushBufferedRowMoves = (moves) => {
      this._hasBufferedRowMoves = false;
      this._persistMoveDeltas(moves);
    };

    /**
     * Subscriber function for observables...set global highlight transient
     * @return [type] [description]
     */
    const setGlobalHighlightTransient = ({ rowIndex, highlightClass }) => {
      this._setGlobalHighlightTransient(rowIndex, highlightClass);
    };

    /**
     * Subscriber function for observables...set global highlight persistent
     * @return [type] [description]
     */
    const setGlobalHighlightPersistent = ({ rowIndex, highlightClass }) => {
      this._setGlobalHighlightPersistent(rowIndex, highlightClass);
    };

    /**
     * Subscriber function for observables...set personal highlight transient
     * @return [type] [description]
     */
    const setPersonalHighlightTransient = ({ rowIndex, highlightClass }) => {
      this._setPersonalHighlightTransient(rowIndex, highlightClass);
    };

    /**
     * Subscriber function for observables...set personal highlight persistent
     * @return [type] [description]
     */
    const setPersonalHighlightPersistent = ({ rowIndex, highlightClass }) => {
      this._setPersonalHighlightPersistent(rowIndex, highlightClass);
    };

    /**
     * Cue up these so they can be run in bunches
     * @type {[type]}
     */
    this.rowObservables.addItemRow
      .bufferTime(1000)
      .filter((items) => items.length)
      .subscribe(addItemRowsPersistent);

    /**
     * When the observable 'moveRowsDebounce' receives a new item in the queue,
     * run through transient callback immediately, but buffer the move deltas
     * until the user has stopped moving rows for 500ms, then persist them
     */
    this.rowObservables.moveRowsDebounce.subscribe(moveRowsTransient);
    this.rowObservables.moveRowsDebounce
      .buffer(this.rowObservables.moveRowsDebounce.debounceTime(500))
      .filter((moves) => moves.length)
      .subscribe(flushBufferedRowMoves);

    this.rowObservables.globalHighlightDebounce.subscribe(
      setGlobalHighlightTransient,
    );
    this.rowObservables.globalHighlightDebounce
      .debounceTime(500)
      .subscribe(setGlobalHighlightPersistent);

    this.rowObservables.personalHighlightDebounce.subscribe(
      setPersonalHighlightTransient,
    );
    this.rowObservables.personalHighlightDebounce
      .debounceTime(500)
      .subscribe(setPersonalHighlightPersistent);
  },

  onHandleDeleteRowWithConfirmation(rowIndex) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    const isGlobalElementManagerView = isGlobalElementManager();
    const isGlobalElement = this.getItemRowGlobalElementStatus(index);

    let confirmMessage = Messages.deleteRow;

    if (isGlobalElement)
      confirmMessage = Messages.deleteGlobalElementRowFromShow;
    if (isGlobalElementManagerView)
      confirmMessage = Messages.deleteGlobalElementRow;

    ConfirmActions.prompt(confirmMessage, () =>
      CuesheetRowActions.deleteItemRow(rowIndex),
    );
  },

  onDeleteSelectedRowsWithConfirmation() {
    let containsGlobalElements = false;

    this.getState().withMutations((st) => {
      st.get('rows').forEach((row, rowIndex) => {
        if (row.get('rowSelected')) {
          containsGlobalElements =
            containsGlobalElements ||
            this.getItemRowGlobalElementStatus(rowIndex);
        }
      });
    });

    const isGlobalElementManagerView = isGlobalElementManager();

    const selectedRowCount = this._getSelectedRowCount();

    let confirmMessage = Messages.bulkDelete(selectedRowCount);

    //Check if any rows contain global elements
    if (containsGlobalElements && !isGlobalElementManagerView) {
      confirmMessage = Messages.deleteGlobalElementRowsFromShow;
    }

    ConfirmActions.prompt(
      confirmMessage,
      () => CuesheetRowActions.deletedSelectedRows(),
      'Bulk delete rows?',
      {
        buttonClass: 'btn-delete',
        buttonText: `Delete ${selectedRowCount} rows`,
      },
    );
  },

  /**
   * Take all 'selected' rows and put them in the paste buffer
   */
  onAddSelectionToPasteBuffer() {
    if (this._isRowReorderingDisabled(true)) return null;

    let next = this._clearPasteBuffer();

    /**
     * For each selected row, add it as a pastableRow
     */
    next = next.withMutations((st) => {
      st.set('pasteMode', PasteModes.Paste);
      const pastableRows = [];
      st.get('rows').forEach((row, rowIndex) => {
        if (row.get('rowSelected')) {
          st.setIn(['rows', rowIndex, 'copied'], true);
          pastableRows.push(this._getPastableItemData(rowIndex));
        }
      });

      st.update('pastableRows', (rows) => (rows || []).concat(pastableRows));
    });

    next = this.deselectAllCellsExceptLast(next);

    persistCopyState(next);

    this.setStateAndTrigger(next);
  },

  /**
   * Take all 'selected' rows and put them in the cut buffer
   */
  onAddSelectionToCutBuffer() {
    if (this._isRowReorderingDisabled(true)) return null;

    let next = this._clearPasteBuffer();
    let lockedRows = false;

    /*
     * For each selected row, add it as a pastableRow
     */
    next = next.withMutations((st) => {
      st.set('pasteMode', PasteModes.Cut);
      const pastableRows = [];
      let pastableRowIds = List();
      st.get('rows').forEach((row, rowIndex) => {
        const path = this.getInState(['rows', rowIndex, 'path']);
        const itemSetId = path[0],
          rowId = path[2];

        if (row.get('rowSelected')) {
          //Check if any selected rows are locked
          lockedRows = this.getItemRowLockStatus(rowIndex) || lockedRows;

          st.setIn(['rows', rowIndex, 'copied'], true);
          pastableRows.push(this._getPastableItemData(rowIndex));
          pastableRowIds = pastableRowIds.push(
            Map({
              teamId: next.get('teamId'),
              eventId: next.get('eventId'),
              showId: next.get('showId'),
              itemSetListId: next.get('itemSetListId'),
              itemSetId,
              rowId,
            }),
          );
        }
      });

      st.update('pastableRows', (rows) => (rows || []).concat(pastableRows));
      st.update('pastableRowIds', (ids) =>
        (ids || List()).concat(pastableRowIds),
      );
    });

    //Check if any of the rows are locked
    if (lockedRows) {
      return AlertActions.show(Messages.lockedRowModal, UIRegions.Cuesheet);
    } else if (this._getSelectedRowCount() >= next.get('rows').size) {
      /**
       * Can't cut all rows from show/grid. You must keep at least one row.
       */
      return AlertActions.show(Messages.cutLastItemRow, UIRegions.Cuesheet);
    }

    next = this.deselectAllCellsExceptLast(next);

    persistCopyState(next);

    this.setStateAndTrigger(next);
  },

  /**
   * Toggle a row's selected state
   */
  onToggleRowSelection(rowIndex) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    let next = this.getState().updateIn(
      ['rows', index, 'rowSelected'],
      (selected) => (selected ? false : true),
    );

    next = next.set('rowSelectionMode', this._anyRowsSelected(next));

    next = this.selectFirstCellInRowWithRowIndex(index, next);

    this.setStateAndTrigger(next);
  },

  /**
   * Toggle all rows selection state with a cell selection
   */
  onToggleMultipleRowSelection() {
    const indices = this._getSelectedCellRowIndices();

    const next = this.getState().withMutations((state) => {
      const anySelected = this._anyOfRowsSelected(
        state.get('rows').filter((row, indx) => indices.indexOf(indx) !== -1),
      );

      indices.forEach((index) => {
        if (index === -1) return;
        state.setIn(['rows', index, 'rowSelected'], !anySelected);
      });

      state.set('rowSelectionMode', this._anyRowsSelected(state));
    });

    this.setStateAndTrigger(next);
  },

  onSelectAllRows() {
    const next = this.getState().update('rows', (rows) => {
      return rows.map((row) => row.set('rowSelected', true));
    });

    this.setStateAndTrigger(
      next.set('rowSelectionMode', this._anyRowsSelected(next)),
    );
  },

  /**
   * Toggle a row's locked state
   */
  onToggleRowLock(rowIndex) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    const rowSpec = this.getInState(['rows', index]),
      rowPath = rowSpec.get('path');

    const itemSetId = rowPath[0],
      rowId = rowPath[2];

    let next = this.getState()
      .updateIn(['rowMap'].concat(rowPath, 'locked'), (locked) => !locked)
      .setIn(['rows', index, 'touch'], new Date().getTime());

    next = this.selectFirstCellInRowWithRowIndex(index, next);

    const locked = next.getIn(['rowMap'].concat(rowPath, 'locked'));
    this._saveItemFieldChangesPersistent(itemSetId, rowId, fromJS({ locked }));
    this.setStateAndTrigger(next);
    if (locked) {
      CuesheetCellActions.clearPastableCellsBuffer();
    }
  },

  /**
   * Clear row selection
   *
   * TODO: Replace this and all references of this method with deselectAllCells
   */
  onClearRowSelection() {
    const next = this._clearPasteBuffer()
      .set('rowSelectionMode', false)
      .update('rows', (rows) => rows.map((row) => row.delete('rowSelected')));

    this.setStateAndTrigger(next);
  },

  _anyRowsSelected(state) {
    state = state || this.getState();

    return this._anyOfRowsSelected(state.get('rows'));
  },

  _anyOfRowsSelected(rows) {
    return rows.findIndex((rowSpec) => rowSpec.get('rowSelected')) >= 0;
  },

  _getSelectedRowCount(state) {
    state = state || this.getState();

    return state.get('rows').filter((rowSpec) => rowSpec.get('rowSelected'))
      .size;
  },

  onItemRowTrackedRealTime(userId, showId, payload, responsPayload) {
    const user = responsPayload;

    const { itemSetId, itemSetRowId, trackedOn } = payload;

    if (!this._trackedTimestampMap) {
      this._trackedTimestampMap = Map();
    }

    const lastTrackedOn = this._trackedTimestampMap.get(userId);

    /**
     * If we have received a 'newer' one than the incoming message, do nothing.
     */
    if (lastTrackedOn && trackedOn < lastTrackedOn) return;

    this._trackedTimestampMap = this._trackedTimestampMap.set(
      userId,
      trackedOn,
    );

    /**
     * Get the row position of the tracked row
     */
    const trackedRowIndex = this.findRowPosition(itemSetId, itemSetRowId);

    if (trackedRowIndex === -1) return;

    /**
     * Set the trackable user name in the row, regardless of whether the user is user is tracking that user
     */
    let next = this.getState();
    const path = next.getIn(['rows', trackedRowIndex, 'path']);

    if (user.proof) {
      next = next.setIn(
        ['rowMap'].concat(path).concat('proof'),
        fromJS(user.proof),
      );
      next = this.touchRow(trackedRowIndex, next);
    }

    //To resolve issue https://github.com/SHOFLO/Issues/issues/4035 we remove the scrollColumnIndex here
    this.setStateAndTrigger(next.delete('scrollColumnIndex'));
    CuesheetRowActions.bustRowHeightCache(path[2]);
  },

  /**
   * Get state w/ tracked username removed/cleared
   */
  _getClearedTrackedUserName(userId, next) {
    next = next || this.getState();

    const rows = this.getInState('rows');

    if (rows) {
      const oldIndx = rows.findIndex((rowSpec) => {
        const trackedUsers = rowSpec.get('trackedUsers');
        return trackedUsers && trackedUsers.has(userId);
      });

      if (oldIndx !== -1) {
        next = next.deleteIn(['rows', oldIndx, 'trackedUsers', userId]);
      }
    }

    return next;
  },

  onTrackSelectedRow() {
    const trackedRowIndx = this.findOneRowWithField('tracked');
    const selectedRowIndx = this.getLastSelectedCellRowIndex();

    if (selectedRowIndx === -1) return;

    if (trackedRowIndx !== -1) {
      this._updateItemDurationsToCurrentDuration(trackedRowIndx);
    }

    CuesheetRowActions.trackRow(selectedRowIndx);

    this.deselectAllCellsExceptLast();
  },

  onTrackNextRow(options = {}) {
    const trackedRowIndx = this.findOneRowWithField('tracked');

    if (trackedRowIndx === -1) return;

    const updateItemDurationToCurrentDuration =
      options.updateDuration && !this._isRunTimeSuppressed(trackedRowIndx);

    this._updateItemDurationsToCurrentDuration(
      trackedRowIndx,
      updateItemDurationToCurrentDuration,
    );

    CuesheetRowActions.trackRow(trackedRowIndx + 1);

    this.deselectAllCellsExceptLast();
  },

  onTrackPreviousRow() {
    const trackedRowIndx = this.findOneRowWithField('tracked');

    if (trackedRowIndx === -1) return;

    this._updateItemDurationsToCurrentDuration(trackedRowIndx);

    this.deselectAllCellsExceptLast();

    CuesheetRowActions.trackRow(trackedRowIndx - 1);
  },

  _updateItemDurationsToCurrentDuration(trackedRowIndx, updateDuration) {
    const updateActualDuration = this.getInState([
      'show',
      'displayActualDuration',
    ]);

    CuesheetCueClockActions.updateItemDurationsToCurrentDuration(
      trackedRowIndx,
      updateDuration,
      updateActualDuration,
    );
  },

  _isRunTimeSuppressed(rowIndex) {
    return this._getItemField(rowIndex, 'runTimeClockSuppressed');
  },

  _getItemField(rowIndex, field) {
    const path = this.getInState(['rows', rowIndex, 'path']);
    const item = this.getInState(['rowMap'].concat(path));
    return item.get(field);
  },

  onTrackRow(rowIndex) {
    if (!this.isEventShowCaller()) return;

    const path = this.getInState(['rows', rowIndex, 'path']);

    if (!path) return;

    const currTrackedIndex = this.findOneRowWithField('tracked');

    if (currTrackedIndex !== -1) {
      this._updateItemDurationsToCurrentDuration(currTrackedIndex);
    }

    const showId = this.getInState('showId'),
      itemSetId = path[0],
      itemSetRowId = path[2];

    const runTimeSuppressed = this._isRunTimeSuppressed(rowIndex);
    const clockDuration = this._getItemField(rowIndex, 'durationInSeconds');

    const selectableCells = this.getInState(['selectableCells']);
    const nextCellSpec = selectableCells.get(0);

    this.setCursor(rowIndex, nextCellSpec, false, true);

    this._delayPersistentTracking(
      itemSetId,
      itemSetRowId,
      clockDuration,
      runTimeSuppressed,
    );

    this.setTrackedRowTransient(
      showId,
      itemSetId,
      itemSetRowId,
      this._getUser(),
    );
  },

  _delayPersistentTracking(
    itemSetId,
    itemSetRowId,
    clockDuration,
    startCueClock,
  ) {
    const userId = this.getInState('userId'),
      eventId = this.getInState('eventId'),
      showId = this.getInState('showId');

    if (this._cueTrackTimeout) clearInterval(this._cueTrackTimeout);
    this._cueTrackTimeout = setTimeout(() => {
      CuesheetTrackingActions.updateTrackableCue(
        userId,
        eventId,
        showId,
        itemSetId,
        itemSetRowId,
      );

      if (startCueClock) return;
      if (this._cueTrackClockTimeout) clearInterval(this._cueTrackClockTimeout);
      this._cueTrackClockTimeout = setTimeout(() => {
        CuesheetCueClockActions.playCueClockWithDuration(
          userId,
          eventId,
          showId,
          clockDuration,
        );
        delete this._cueTrackTimeout;
        delete this._cueTrackClockTimeout;
      }, 1000);
    }, 400);
  },

  onTrackRowTransient(showId, itemSetId, itemSetRowId) {
    this.setTrackedRowTransient(showId, itemSetId, itemSetRowId);
  },

  onUpdateRowPersonalHighlight(rowIndex, highlightClass) {
    this.selectFirstCellInRowWithRowIndex(rowIndex);
    this.rowObservables.personalHighlightDebounce.next({
      rowIndex,
      highlightClass,
    });
  },

  onUpdateRowHighlight(rowIndex, highlightClass) {
    this.selectFirstCellInRowWithRowIndex(rowIndex);
    this.rowObservables.globalHighlightDebounce.next({
      rowIndex,
      highlightClass,
    });
  },

  onToggleRowHighlight(rowIndex) {
    const selectedRows = this._getSelectedRows();
    let index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    if (this._hasSelectedRows()) {
      const row = selectedRows.first();
      const path = row.get('path');
      const itemSetId = path[0];
      const rowId = path[2];
      rowIndex = this.findRowPosition(itemSetId, rowId);

      index = rowIndex;
    }

    const currentClass = this._getAppliedRowHighlightClass(index);
    const highlightClass = this._getNextRowHighlightClass(currentClass);

    this.rowObservables.globalHighlightDebounce.next({
      rowIndex: index,
      highlightClass,
    });
  },

  onToggleRowPersonalHighlight(rowIndex) {
    const selectedRows = this._getSelectedRows();
    let index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    if (this._hasSelectedRows()) {
      const row = selectedRows.first();
      const path = row.get('path');
      const itemSetId = path[0];
      const rowId = path[2];
      rowIndex = this.findRowPosition(itemSetId, rowId);

      index = rowIndex;
    }

    const currentClass = this._getAppliedRowPersonalHighlightClass(index);
    const highlightClass = this._getNextRowHighlightClass(currentClass);

    this.rowObservables.personalHighlightDebounce.next({
      rowIndex: index,
      highlightClass,
    });
  },

  _getNextRowHighlightClass(currentClass) {
    const colorClasses = this._getRowHighlightClasses();
    const nextClassIndex = colorClasses.indexOf(currentClass) + 1;

    const nextHighlightClass =
      nextClassIndex >= colorClasses.length
        ? colorClasses[0]
        : colorClasses[nextClassIndex];

    return nextHighlightClass;
  },

  /**
   * Return array of formatted highlight classes
   */
  _getRowHighlightClasses() {
    const Colors = {
      pink3: 'pink3',
      green3: 'green3',
      yellow3: 'yellow3',
      orange3: 'orange3',
      brown3: 'brown3',
      red3: 'red3',
      black3: 'black3',
      blue3: 'blue3',
      purple3: 'purple3',
    };

    const colorClasses = Object.keys(Colors).map((color) => {
      return 'highlight-' + color;
    });

    colorClasses.push('highlight-none');

    return colorClasses;
  },

  /**
   * Retrieves global highlight class applied to current row
   */
  _getAppliedRowHighlightClass(rowIndex) {
    const path = this.getInState(['rows', rowIndex, 'path']);
    const highlightClass = this.getInState(
      ['rowMap'].concat(path, 'settings', 'highlightClass'),
    );
    return highlightClass || 'highlight-none';
  },

  /**
   * Retrieves personal highlight class applied to current row for userId in
   * state
   */
  _getAppliedRowPersonalHighlightClass(rowIndex) {
    const userId = this.getInState('userId');
    const path = this.getInState(['rows', rowIndex, 'path']);

    const highlightClass = this.getInState(
      ['rowMap'].concat(path, 'userSettings', userId, 'highlightClass'),
    );
    return highlightClass || 'highlight-none';
  },

  onAddTitleRowAbove(rowIndex) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    this._addItemRowsAbove(index, [
      {
        item: { startOn: 0 },
        titleRow: true,
      },
    ]);
  },

  onAddTitleRowBelow(rowIndex) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    this._addItemRowsBelow(index, [
      {
        item: { startOn: 0 },
        titleRow: true,
      },
    ]);
  },

  /**
   * Toggle title row on and off for a specific row
   */
  onToggleTitleRow(rowIndex) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (this.getItemRowLockStatusWithAlert(index)) return;

    if (index === -1) return;

    if (this.getInState(['rows', index, 'type']) === RowTypes.TitleItem) {
      this._convertToStandardRow(index);
    } else {
      this._convertToTitleRow(index);
    }
  },

  onConvertToTitleRow(rowIndex) {
    if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    this._convertToTitleRow(index);

    const rowPath = this.getInState(['rows', index, 'path']);

    CuesheetRowActions.bustRowHeightCache(rowPath[2]);
  },

  _convertToTitleRow(rowIndex) {
    //1. Update the type of row in state (transient)
    this.convertToTitleRowTransient(rowIndex);

    //2. Make persistent change
    const state = this.getState();
    const path = this.getInState(['rows', rowIndex, 'path']);
    const itemSetId = path[0],
      rowId = path[2];

    CuesheetRowActions.convertToTitleRowPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      itemSetId,
      rowId,
    );

    CuesheetRowActions.bustRowHeightCache(rowId);
  },

  convertToTitleRowTransient(rowIndex) {
    this.convertItemRowTransient(rowIndex, true);
  },

  convertToItemRowTransient(rowIndex) {
    this.convertItemRowTransient(rowIndex, false);
  },

  onConvertToStandardRow(rowIndex) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    this._convertToStandardRow(index);

    const rowPath = this.getInState(['rows', index, 'path']);

    CuesheetRowActions.bustRowHeightCache(rowPath[2]);
  },

  _convertToStandardRow(rowIndex) {
    //1. Update the type of row in state (transient)
    this.convertToItemRowTransient(rowIndex);

    //2. Make persistent change
    const state = this.getState();
    const path = this.getInState(['rows', rowIndex, 'path']);
    const itemSetId = path[0],
      rowId = path[2];

    CuesheetRowActions.convertToStandardRowPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      itemSetId,
      rowId,
    );

    CuesheetRowActions.bustRowHeightCache(rowId);
  },

  /**
   * Convert a row between a regular row and a title row.
   *
   * @param {NUMBER} rowIndex
   * @param {BOOLEAN} titleView - Set to true if you want to convert row to a
   * title row.
   */
  convertItemRowTransient(rowIndex, titleView) {
    const path = this.getInState(['rows', rowIndex, 'path']);
    const titleViewPath = ['rowMap'].concat(path).concat('titleView');

    const type = titleView ? RowTypes.TitleItem : RowTypes.Item;

    const next = this.getState()
      .setIn(['rows', rowIndex, 'type'], type)
      .setIn(['rows', rowIndex, 'touch'], new Date().getTime())
      .updateIn(titleViewPath, () => titleView);

    this.setStateAndTrigger(next);

    const rowId = path[2];

    CuesheetRowActions.bustRowHeightCache(rowId);
  },

  onUpdateRow(rowIndex, changes) {
    const state = this.getState();

    const path = state.getIn(['rows', rowIndex, 'path']);
    const itemSetId = path[0],
      rowId = path[2];

    CuesheetRowActions.bustRowHeightCache(rowId);

    //1. Update the type of row in state (transient)
    this.updateRowTransient(rowIndex, changes);

    //2. Make persistent change
    CuesheetRowActions.updateRowPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      itemSetId,
      rowId,
      changes,
    );
  },

  updateRowTransient(rowIndex, changes) {
    const path = this.getInState(['rows', rowIndex, 'path']);
    const rowMapPath = ['rowMap'].concat(path);

    let next = this.getState()
      .setIn(['rows', rowIndex, 'touch'], new Date().getTime())
      .updateIn(rowMapPath, (item) => item.merge(changes));

    this.setStateAndTrigger(next);
  },

  onAddItemRowAbove(rowIndex) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    this._addItemRowsAbove(index, [{ item: { startOn: 0 }, titleRow: false }]);
  },

  onAddItemRowBelow(rowIndex) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    this._addItemRowsBelow(index, [{ item: { startOn: 0 }, titleRow: false }]);
  },

  /**
   * Used when a rowIndex is not specified. Adds row to the end of
   * the list of rows.
   */
  onAddItemRow() {
    if (!this.getInState('rows')) return;

    /**
     * Use unfilteredRows when in global element manager and no rows match the
     * filter applied to category or tags
     */
    const rowField = this._getRowFieldForAppliedFilter();

    const rows = this.getInState(rowField);
    const rowIndex = rows.size === 0 ? 0 : rows.size - 1;

    this._addItemRowsBelow(rowIndex, [
      { item: { startOn: 0 }, titleRow: false },
    ]);
  },

  onAddItemRowProof(rowIndex, note) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    const path = this.getInState(['rows', index, 'path']);
    const itemSetId = path[0],
      rowId = path[2];

    const state = this.getState();

    CuesheetRowActions.addItemRowProofPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      itemSetId,
      rowId,
      note,
    );

    const nextState = state.withMutations((st) => {
      st.setIn(['rowMap'].concat(path).concat('proof'), Map({ note: '' }));
      this.touchRow(index, st);
    });

    CuesheetRowActions.bustRowHeightCache(rowId);

    this.setStateAndTrigger(this.getClearedScrollIndexState(nextState));
  },

  onUpdateItemRowProofNote(rowIndex, note) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    const path = this.getInState(['rows', rowIndex, 'path']);
    const itemSetId = path[0],
      rowId = path[2];

    const state = this.getState();

    CuesheetRowActions.updateItemRowProofNotePersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      itemSetId,
      rowId,
      note,
    );
  },

  onUpdateItemRowProofNotePersistentCompleted({
    itemSetId,
    rowId,
    proof: updatedProof,
  }) {
    const rowIndex = this.findRowPosition(itemSetId, rowId);

    const path = this.getInState(['rows', rowIndex, 'path']);

    let nextState = this.getState().updateIn(
      ['rowMap', ...path, 'proof'],
      (proof) => proof.merge(updatedProof),
    );
    nextState = this.touchRow(rowIndex, nextState);

    this.setStateAndTrigger(nextState);
  },

  onClearItemRowProof(rowIndex) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    const path = this.getInState(['rows', rowIndex, 'path']);
    const itemSetId = path[0],
      rowId = path[2];

    const state = this.getState();

    CuesheetRowActions.clearItemRowProofPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      itemSetId,
      rowId,
    );

    const nextState = state.withMutations((st) => {
      st.deleteIn(['rowMap'].concat(path).concat('proof'));
      this.touchRow(rowIndex, st);
    });

    this.setStateAndTrigger(nextState);

    // Don't recalculate row heights until after state is updated
    CuesheetRowActions.bustRowHeightCache(rowId);
  },

  onAddItemRowProofPersistentCompleted({
    itemSetId,
    rowId,
    proof,
    proofHistory,
  }) {
    const rowIndex = this.findRowPosition(itemSetId, rowId);

    const nextState = this.saveItemFieldChangesTransient(
      rowIndex,
      fromJS({ proof, proofHistory }),
    );

    this.setStateAndTrigger(nextState);
  },

  onDuplicateItemRow(rowIndex) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    const itemObj = this._getDuplicatedItemData(index);

    this._addItemRowsBelow(index, [itemObj]);
  },

  onAddItemRowPersistentCompleted(result) {
    const { itemSetId, afterRowId, row } = result;

    this.addItemRowsTransient(itemSetId, afterRowId, [row]);
  },

  onAddItemRowsPersistentCompleted(result) {
    const { itemSetId, afterRowId, rows } = result;

    this.addItemRowsTransient(itemSetId, afterRowId, rows);

    this._refreshGlobalElementRowAssociations();
  },

  addItemRowsTransient(itemSetId, afterRowId, rows) {
    //TODO: consolidate this w/ the data helper...there is some redundant stuff here!
    let next = this.getState().withMutations((state) => {
      const rowSpecs = [];

      rows.forEach((row) => {
        const path = CuesheetDataHelper.getRowPath(itemSetId, row._id);

        const item = row.item[0];
        item.titleView = row.titleRow;
        item.labels = row.labels;

        const rowSpec = CuesheetDataHelper.getRowSpec(
          item.titleView ? RowTypes.TitleItem : RowTypes.Item,
          path,
        );

        state.setIn(['rowMap'].concat(path), fromJS(item));

        rowSpecs.push(rowSpec);
      });

      if (!afterRowId) {
        const concatter = (rows) => fromJS(rowSpecs).concat(rows);
        state.update('rows', concatter);
        //We don't want to add to this array if it doesn't exist, it's used for GE view
        if (state.get('unfilteredRows')) {
          state.update('unfilteredRows', concatter);
        }
      } else {
        const afterIndex = this.findRowPosition(itemSetId, afterRowId);
        /**
         * Factory that returns a pure function that will add the rows at the passed index (after)
         * @param  {[type]} after [description]
         * @return [type]         [description]
         */
        const getSplicer = (after) => {
          return (rows) => {
            rowSpecs.forEach((rowSpec, rowSpecIndx) => {
              const index = after + 1;
              rows = rows.splice(index + rowSpecIndx, 0, rowSpec);
            });
            return rows;
          };
        };

        //We need a different index value for the unfilteredRows to pass to the getSplicer function
        const afterUnfilteredIndex = this.findRowPositionInListOfRows(
          itemSetId,
          afterRowId,
          state.get('unfilteredRows'),
        );

        state.update('rows', getSplicer(afterIndex));
        //We don't want to add to this array if it doesn't exist, it's used for GE view
        if (state.get('unfilteredRows')) {
          state.update('unfilteredRows', getSplicer(afterUnfilteredIndex));
        }
      }
    });

    next = this.updateUnfilteredRows(next.get('unfilteredRows'), next);

    next = this.recalculateTimeFields(next);

    /*
     * Recalculate the selected cell range to take into account the new row(s).
     */
    next = this.recalculateCellSelectionRange(next);

    this.setStateAndTrigger(this.getClearedScrollIndexState(next));
  },

  onRestoreItemRow(itemSetId, rowId) {
    const state = this.getState();

    CuesheetRowActions.restoreItemRowPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      itemSetId,
      rowId,
    );
  },

  onRestoreItemRowPersistentCompleted(result) {
    this.restoreItemRow(result);
  },

  restoreItemRow(result) {
    const { itemSetId, row } = result;

    const lastRowIndex = this.getInState('rows').keySeq().last(),
      lastRowId = this.findRowId(lastRowIndex);

    //Add item row after last row in current show
    this.addItemRowsTransient(itemSetId, lastRowId, [row]);
  },

  onDeleteItemRowTransient(rowId) {
    const itemSetId = this.findFirstItemSetId(),
      rowIndex = this.findRowPosition(itemSetId, rowId);

    this._deleteItemRowTransient(rowIndex);
  },

  onDeleteItemRow(rowIndex) {
    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;
    /**
     * Can't delete all rows from show/grid. You must keep at least one row.
     */ else if (
      index === 0 &&
      this.getInState('rows').size === 1 &&
      !isGlobalElementManager()
    ) {
      return AlertActions.show(Messages.deleteLastItemRow, UIRegions.Cuesheet);
    }

    //Check if row is locked
    if (this.getItemRowLockStatusWithAlert(index)) return;

    const path = this.getInState(['rows', index, 'path']);
    const itemSetId = path[0];
    const rowId = path[2];

    this._deleteItemRows(itemSetId, [rowId]);
    this._deleteItemRowTransient(index);
  },

  _deleteItemRowTransient(rowIndex) {
    const path = this.getInState(['rows', rowIndex, 'path']);
    const rowId = path[2];

    this.deselectAllCellsExceptLast();
    this.deselectAllCellsWithRowIndex(rowIndex);

    /**
     * Changes in support of #5386
     */
    let unfilteredRows = this.getInState('unfilteredRows');

    if (this.getInState('unfilteredRows')) {
      const unfilteredIndex = this.findRowPositionInListOfRows(
        path[0],
        rowId,
        unfilteredRows,
      );

      if (unfilteredIndex !== -1) {
        this.setInState(
          'unfilteredRows',
          unfilteredRows.delete(unfilteredIndex),
        );
      }
    }

    this.recalculateTimeFieldsAndTrigger(
      this.getState().deleteIn(['rows', rowIndex]),
    );
  },

  onDeletedSelectedRows() {
    let itemSetId;
    let lockedRows = false;

    const rowIds = [];

    let next = this.getState().withMutations((st) => {
      const rows = st.get('rows');

      rows.forEach((row, rowIndex) => {
        if (row.get('rowSelected')) {
          //Check if any selected rows are locked
          lockedRows = this.getItemRowLockStatus(rowIndex) || lockedRows;

          const path = this.getInState(['rows', rowIndex, 'path']);
          itemSetId = path[0];
          const rowId = path[2];
          rowIds.push(rowId);
        }
      });
    });

    //Check if any of the rows are locked
    if (lockedRows) {
      return AlertActions.show(Messages.lockedRowModal, UIRegions.Cuesheet);
    } else if (
      /**
       * Can't delete all rows from show/grid. You must keep at least one row.
       */
      rowIds.length >= next.get('rows').size &&
      !isGlobalElementManager()
    ) {
      return AlertActions.show(Messages.deleteLastItemRow, UIRegions.Cuesheet);
    }

    rowIds.forEach((rowId) => {
      const rowIndex = this.findRowPosition(itemSetId, rowId, next);
      next = this.deselectAllCellsWithRowIndex(rowIndex, next);
    });

    rowIds.forEach((rowId) => {
      const rowIndex = this.findRowPosition(itemSetId, rowId, next);

      next = next.deleteIn(['rows', rowIndex]);
    });

    /**
     * Changes in support of #5386, clear any unfiltered row state
     */

    rowIds.forEach((rowId) => {
      let unfilteredRows = next.get('unfilteredRows');

      if (unfilteredRows) {
        const unfilteredIndex = this.findRowPositionInListOfRows(
          itemSetId,
          rowId,
          unfilteredRows,
        );

        if (unfilteredRows !== -1) {
          next = next.set(
            'unfilteredRows',
            unfilteredRows.delete(unfilteredIndex),
          );
        }
      }
    });

    this._deleteItemRows(itemSetId, rowIds);

    next = this.deselectAllCellsExceptLast(next);

    this.recalculateTimeFieldsAndTrigger(next);

    CuesheetRowActions.clearRowSelection();
  },

  onAddRowToPasteBuffer(rowIndex) {
    if (this._isRowReorderingDisabled(true)) return null;

    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    this.selectFirstCellInRowWithRowIndex(index);

    let next = this._clearPasteBuffer();

    next = next
      .setIn(['rows', index, 'copied'], true)
      .set('pasteMode', PasteModes.Paste)
      .update('pastableRows', (rows) => {
        const pastableRows = rows || [];
        pastableRows.push(this._getPastableItemData(index));
        return pastableRows;
      });

    persistCopyState(next);

    this.setStateAndTrigger(next);
  },

  onAddRowToCutBuffer(rowIndex) {
    if (this._isRowReorderingDisabled(true)) return null;

    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;
    /**
     * Can't cut all rows from show/grid. You must keep at least one row.
     */ else if (index === 0 && this.getInState('rows').size === 1) {
      return AlertActions.show(Messages.cutLastItemRow, UIRegions.Cuesheet);
    }

    //Check if row is locked
    if (this.getItemRowLockStatusWithAlert(index)) return;

    let next = this._clearPasteBuffer();

    const path = this.getInState(['rows', index, 'path']);
    const itemSetId = path[0],
      rowId = path[2];

    next = next
      .setIn(['rows', index, 'copied'], true)
      .set('pasteMode', PasteModes.Cut)
      .set('pastableRows', [this._getPastableItemData(index)])
      .set(
        'pastableRowIds',
        fromJS([
          {
            teamId: next.get('teamId'),
            eventId: next.get('eventId'),
            showId: next.get('showId'),
            itemSetListId: next.get('itemSetListId'),
            itemSetId,
            rowId,
          },
        ]),
      );

    persistCopyState(next);

    this.setStateAndTrigger(next);

    this.selectFirstCellInRowWithRowIndex(index);
  },

  onPasteBufferAbove(rowIndex) {
    if (this._isRowReorderingDisabled(true)) return;

    if (!this.getInState('pastableRows')) return;

    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    this.selectFirstCellInRowWithRowIndex(index);

    this._pasteBuffer(index, false);
  },

  onPasteBufferBelow(rowIndex) {
    if (this._isRowReorderingDisabled(true)) return;

    if (!this.getInState('pastableRows')) return;

    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1) return;

    this.selectFirstCellInRowWithRowIndex(index);

    this._pasteBuffer(index, true);
  },

  /**
   * Set the tracked row in UI state
   */
  //eslint-disable-next-line no-unused-vars
  setTrackedRowTransient(showId, itemSetId, itemSetRowId, user) {
    if (showId !== this.getInState('showId') || !this.getInState('rows'))
      return;

    let next = this.getState();

    const rowsToBust = [];
    const rowsToTouch = [];

    const prevTrackedRowIndex = this.findOneRowWithField('tracked');
    if (prevTrackedRowIndex !== -1) {
      const prevRowId = this.getInState([
        'rows',
        prevTrackedRowIndex,
        'path',
      ])[2];
      rowsToBust.push(prevRowId);
      rowsToTouch.push(prevTrackedRowIndex);
    }

    next = this.clearRowFieldState('tracked', next).asMutable();

    const trackedRowIndex = this.findRowPosition(itemSetId, itemSetRowId);

    //Used for feature view to determine what the active row index is.
    next.set('featuredCellRowIndex', trackedRowIndex);

    next.setIn(['rows', trackedRowIndex, 'tracked'], true);

    next.set('scrollRowWithOffsetIndex', trackedRowIndex);
    next.delete('scrollRowIndex');
    next.delete('scrollColumnIndex');

    //Intentionally removed this, but I'm worried it was there for a reason, see https://github.com/SHOFLO/Issues/issues/2353
    //next.set('scrollColumnIndex', nextColumnIndex);

    next = next.asImmutable();

    const trackedRowId = this.getInState(['rows', trackedRowIndex, 'path'])[2];
    rowsToBust.push(trackedRowId);
    rowsToTouch.push(trackedRowIndex);

    next = this.touchRows(rowsToTouch, next);

    this.setStateAndTrigger(next);
    CuesheetRowActions.bustRowHeightCache(rowsToBust);
  },

  onClearTrackedRowTransient(userId) {
    const trackedRowIndx = this.findOneRowWithField('tracked');
    if (trackedRowIndx !== -1 && userId === this.getState().get('userId')) {
      this._updateItemDurationsToCurrentDuration(trackedRowIndx);
    }
    let next = this.clearRowFieldState('tracked');

    userId = userId || this.getInState('userId');

    next = this._getClearedTrackedUserName(userId, next);

    next = this.getClearedScrollIndexState(next);

    //Need to call this to resolve this issue:

    /**
     *
     *  See https://github.com/SHOFLO/Issues/issues/1683
     *
     *  When show caller has turned broadcasting OFF, and all see the blue bar disappear.
     *  If show caller leaves & comes back into show, the blue bar re-appears but the disable
     *  broadcasting button is not present.
     *
     */
    this.clearInitTrackableCue();

    this.setStateAndTrigger(next);
  },

  getClearedScrollIndexState(state) {
    state = state || this.getState();

    return state
      .delete('scrollRowIndex')
      .delete('scrollColumnIndex')
      .delete('scrollRowWithOffsetIndex');
  },

  /**
   * Set the scroll position to a particular row
   */
  onScrollToRow(showId, itemSetId, itemSetRowId, cellSpec, skipScroll) {
    if (showId !== this.getInState('showId') || !this.getInState('rows'))
      return;
    const skipViewportScroll =
      typeof skipScroll === 'boolean' && !skipScroll ? false : true;
    /**
     * 1. Get the row index
     */
    const rowIndex = this.findRowPosition(itemSetId, itemSetRowId);

    /**
     * 2. Set the scroll position to the row index..scrolling it w/ offset ensures we'll have an adequate buffer
     * between top/bottom edges of the scrollable area
     */
    this.setStateAndTrigger(
      this.getState().set('scrollColumnIndex', 0).delete('scrollRowIndex'),
    );

    /**
     * 3. Select the item cell
     * 'Selecting' the item cell also sets the scroll position. By doing this we move the scroll position, and also in
     * effect highlight the row we've just scrolled to
     */
    cellSpec = cellSpec || { type: EditableCellTypes.ItemTitle };

    this.setCursor(rowIndex, cellSpec, skipViewportScroll);

    //Do this _after_ set cursor, because setCursor blows away the `scrollRowWithOffsetIndex` value in state
    this.setInStateAndTrigger('scrollRowWithOffsetIndex', rowIndex);
  },

  _setPersonalHighlightTransient(rowIndex, highlightClass) {
    const next = this.getState(),
      userId = next.get('userId');

    if (this._hasSelectedRows()) {
      const settingPath = ['userSettings', userId, 'highlightClass'];
      this._setHighlights(highlightClass, settingPath);
    } else {
      const path = this.getInState(['rows', rowIndex, 'path']);

      this.setStateAndTrigger(
        next
          .setIn(
            ['rowMap'].concat(path, 'userSettings', userId, 'highlightClass'),
            highlightClass,
          )
          .setIn(['rows', rowIndex, 'touch'], new Date().getTime()),
      );
      CuesheetRowActions.bustRowHeightCache(path[2]);
    }
  },

  _setPersonalHighlightPersistent(rowIndex, highlightClass) {
    const state = this.getState();

    const selectedRows = this._getSelectedRows();

    if (this._hasSelectedRows()) {
      const rowIds = [];
      let itemSetId;

      selectedRows.forEach((row) => {
        const path = row.get('path');
        itemSetId = path[0];
        const rowId = path[2];

        const rowIndex = this.findRowPosition(itemSetId, rowId);

        if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

        rowIds.push(rowId);
      });

      CuesheetRowActions.updateItemRowUserSettingsPersistent(
        state.get('teamId'),
        state.get('eventId'),
        state.get('showId'),
        state.get('itemSetListId'),
        itemSetId,
        null,
        rowIds,
        state.get('userId'),
        { highlightClass },
      );
    } else {
      const path = this.getInState(['rows', rowIndex, 'path']);

      const itemSetId = path[0],
        rowId = path[2];

      CuesheetRowActions.updateItemRowUserSettingsPersistent(
        state.get('teamId'),
        state.get('eventId'),
        state.get('showId'),
        state.get('itemSetListId'),
        itemSetId,
        rowId,
        null,
        state.get('userId'),
        { highlightClass },
      );
    }
  },

  onUpdateItemRowUserSettingsPersistentCompleted() {
    ToastActions.saved();
  },

  _getSelectedRows() {
    const nextState = this.getState();
    const rows = nextState.get('rows');
    const selectedRows = rows.filter((row) => row.get('rowSelected'));

    return selectedRows;
  },

  _hasSelectedRows() {
    const selectedRows = this._getSelectedRows();
    return selectedRows.size > 0 ? true : false;
  },

  _setHighlights(highlightClass, settingPath) {
    let nextState = this.getState();
    const selectedRows = this._getSelectedRows();
    const rowsToBust = [];

    selectedRows.forEach((row) => {
      const path = row.get('path');
      const itemSetId = path[0];
      const rowId = path[2];
      rowsToBust.push(rowId);
      const rowIndex = this.findRowPosition(itemSetId, rowId);

      if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

      nextState = nextState
        .setIn(['rowMap'].concat(path).concat(settingPath), highlightClass)
        .setIn(['rows', rowIndex, 'touch'], new Date().getTime());
    });
    this.setStateAndTrigger(nextState);
    CuesheetRowActions.bustRowHeightCache(rowsToBust);
  },

  _setGlobalHighlightTransient(rowIndex, highlightClass) {
    let nextState = this.getState();

    if (this._hasSelectedRows()) {
      const settingPath = ['settings', 'highlightClass'];
      this._setHighlights(highlightClass, settingPath);
    } else {
      //Check if row is locked
      if (this.getItemRowLockStatusWithAlert(rowIndex)) return;
      nextState = this.getClearedScrollIndexState(this.getState());

      const path = this.getInState(['rows', rowIndex, 'path']);

      this.setStateAndTrigger(
        nextState
          .setIn(
            ['rowMap'].concat(path, 'settings', 'highlightClass'),
            highlightClass,
          )
          .setIn(['rows', rowIndex, 'touch'], new Date().getTime()),
      );
    }
  },

  _setGlobalHighlightPersistent(rowIndex, highlightClass) {
    const state = this.getState();

    const selectedRows = this._getSelectedRows();

    if (this._hasSelectedRows()) {
      const rowIds = [];
      let itemSetId;

      selectedRows.forEach((row) => {
        const path = row.get('path');
        itemSetId = path[0];
        const rowId = path[2];

        const rowIndex = this.findRowPosition(itemSetId, rowId);

        if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

        rowIds.push(rowId);
      });

      CuesheetRowActions.updateItemRowSettingsPersistent(
        state.get('teamId'),
        state.get('eventId'),
        state.get('showId'),
        state.get('itemSetListId'),
        itemSetId,
        null,
        rowIds,
        { highlightClass },
      );
    } else {
      //Check if row is locked
      if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

      const path = this.getInState(['rows', rowIndex, 'path']);

      const itemSetId = path[0],
        rowId = path[2];

      CuesheetRowActions.updateItemRowSettingsPersistent(
        state.get('teamId'),
        state.get('eventId'),
        state.get('showId'),
        state.get('itemSetListId'),
        itemSetId,
        rowId,
        null,
        { highlightClass },
      );
    }
  },

  _pasteBuffer(rowIndex, below) {
    /**
     * 1. Determine the handler (above or below funcs)
     */
    const pasteHandler = below ? '_addItemRowsBelow' : '_addItemRowsAbove';

    const pasteMode = this.getInState('pasteMode');
    const itemObjects = this.getInState('pastableRows');

    itemObjects.forEach((itemObject) => {
      const cells = {};
      this.getInState('fullColumnOrder').forEach((colId, indx) => {
        cells[colId] = itemObject.item.orderedCells.get(indx);
      });

      itemObject.item.cells = cells;
      delete itemObject.item.orderedCells;
    });

    /**
     * If paste mode is Paste just add rows
     */

    if (pasteMode === PasteModes.Paste) {
      this[pasteHandler](rowIndex, itemObjects);
    }

    /**
     * 3. If paste mode is Cut
     */
    if (pasteMode === PasteModes.Cut) {
      const firstRowToDelete = this.getInState('pastableRowIds').first();

      //If pasting into the same show, dont actually delete the rows, but instead
      //move the cut rows to the new index in the row order
      if (firstRowToDelete.get('showId') === this.getInState('showId')) {
        this._reorderCutItemRows(rowIndex, itemObjects);
      }

      //If pasting into a different show, delete the rows from the original show,
      //an create new rows in the target show
      if (firstRowToDelete.get('showId') !== this.getInState('showId')) {
        this[pasteHandler](rowIndex, itemObjects);

        const rowIdsToDelete = this.getInState('pastableRowIds')
          .map((r) => r.get('rowId'))
          .toArray();
        const deleteItemRowArgs = [
          firstRowToDelete.get('teamId'),
          firstRowToDelete.get('eventId'),
          firstRowToDelete.get('showId'),
          firstRowToDelete.get('itemSetListId'),
          firstRowToDelete.get('itemSetId'),
          rowIdsToDelete,
          PasteModes.Cut,
        ];

        CuesheetRowActions.deleteItemRowsPersistent.apply(
          null,
          deleteItemRowArgs,
        );
      }
    }

    /**
     * 4. Clear the selection/paste buffer
     */
    CuesheetRowActions.clearRowSelection();
  },

  _clearPasteBuffer() {
    return this.getState()
      .update('rows', (rows) => rows.map((row) => row.delete('copied')))
      .delete('pasteMode')
      .delete('pastableRows')
      .delete('pastableRowIds');
  },

  _deleteItemRows(itemSetId, rowIds) {
    const state = this.getState();

    CuesheetRowActions.deleteItemRowsPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      itemSetId,
      rowIds,
      undefined,
      isGlobalElementManager(),
    );
  },

  onSaveSelectionAsNewGlobalElements() {
    let activeRowIndices = [];

    this.getInState('rows').forEach((row, rowIndex) => {
      if (row.get('rowSelected')) {
        activeRowIndices.push(rowIndex);
      }
    });

    NewGlobalElementModalActions.populate(activeRowIndices);
  },

  /*
   * If row filtered is applied then disable row re-ordering.
   * Primarily applies to the global element editor
   */
  _isRowReorderingDisabled(showAlert) {
    const next = this.getState();

    const unfilteredRows = next.get('unfilteredRows');
    const rows = next.get('rows');

    const disabled =
      unfilteredRows && unfilteredRows.size > rows.size ? true : false;

    if (disabled && showAlert)
      AlertActions.show(
        Messages.globalElementFunctionDisabled,
        UIRegions.Cuesheet,
      );
    return disabled;
  },

  onMoveItemRowUp(rowIndex) {
    if (this._isRowReorderingDisabled(true)) return;

    if (this.findOneRowWithField('rowSelected') >= 0) {
      return this._moveSelectedItemRowsUp();
    }

    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index <= 0) return;

    this._swapRows(index, index - 1);

    this.setCursor(index - 1, { type: EditableCellTypes.ItemTitle });
  },

  onMoveItemRowDown(rowIndex) {
    if (this._isRowReorderingDisabled(true)) return;

    if (this.findOneRowWithField('rowSelected') >= 0) {
      return this._moveSelectedItemRowsDown();
    }

    const index = this._validRowIndexOrGetSelectedCellRowIndex(rowIndex);

    if (index === -1 || index === this.getState().get('rows').size - 1) return;

    this._swapRows(index, index + 1);

    this.setCursor(index + 1, { type: EditableCellTypes.ItemTitle });
  },

  _moveSelectedItemRowsUp() {
    const rowIds = this._getSelectedRowIds();

    const itemSetId = this.findFirstItemSetId(),
      firstRowId = rowIds.first(),
      firstRowIndex = this.findRowPosition(itemSetId, firstRowId),
      beforeGroupRowIndex = firstRowIndex - 1;

    if (beforeGroupRowIndex < 0) return;

    //Move the selection itself up, landing after the row directly above its
    //new position (or to the top when there is none). Keeping the selection
    //as the subject lets a run of up-moves collapse into one persisted delta,
    //mirroring _moveSelectedItemRowsDown.
    const afterRowId =
      beforeGroupRowIndex > 0
        ? this.findRowId(beforeGroupRowIndex - 1)
        : undefined;

    this.rowObservables.moveRowsDebounce.next({
      rowIds,
      afterRowId,
    });

    this.setCursor(beforeGroupRowIndex, { type: EditableCellTypes.ItemTitle });
  },

  _moveSelectedItemRowsDown() {
    const rowIds = this._getSelectedRowIds();

    const itemSetId = this.findFirstItemSetId(),
      lastRowId = rowIds.last(),
      lastRowIndex = this.findRowPosition(itemSetId, lastRowId),
      afterRowIndex = lastRowIndex + 1;

    //It can't be moved down any further
    if (lastRowIndex === this.getInState('rows').size - 1) return;

    if (afterRowIndex < 0) return;

    const afterRowId = this.findRowId(afterRowIndex);

    this.rowObservables.moveRowsDebounce.next({
      rowIds,
      afterRowId,
    });

    this.setCursor(afterRowIndex, { type: EditableCellTypes.ItemTitle });
  },

  /**
   * Clone a row's item data (omitting some fields we don't want to copy)
   */
  _getDuplicatedItemData(rowIndex) {
    const path = this.getInState(['rows', rowIndex, 'path']);
    const itemToDuplicate = this.getInState(['rowMap'].concat(path));

    const item = _.pick(itemToDuplicate.toJS(), [
      'cells',
      'description',
      'descriptionFormat',
      'displayDurationClock',
      'displayPresetClock',
      'displayStartTimeClock',
      'durationInSeconds',
      'durationSuppressed',
      'locked',
      'number',
      'presetDurationInSeconds',
      'rteDescriptionState',
      'rteTitleState',
      'runTimeClockSuppressed',
      'settings',
      'startOn',
      'title',
      'titleFormat',
      'userSettings',
    ]);

    Object.keys(item).forEach((key) => {
      if (item[key] === null) delete item[key];
    });

    //Don't allow falsey values
    if (!item.titleFormat) delete item.titleFormat;
    if (!item.rteTitleState) delete item.rteTitleState;
    if (!item.descriptionFormat) delete item.descriptionFormat;
    if (item.number === '') delete item.number;

    if (item.titleFormat) {
      Object.keys(item.titleFormat).forEach((key) => {
        if (!ValidCellFormatKeys[key]) delete item.titleFormat[key];
      });
    }

    if (item.descriptionFormat) {
      Object.keys(item.descriptionFormat).forEach((key) => {
        if (!ValidCellFormatKeys[key]) delete item.descriptionFormat[key];
      });
    }

    const rowData = { item, titleRow: itemToDuplicate.get('titleView') };

    if (itemToDuplicate.get('globalElement')) {
      rowData.globalElement = _.pick(
        itemToDuplicate.get('globalElement').toJS(),
        [
          'event',
          'show',
          'itemSetList',
          'itemSet',
          'row',
          'sourceEvent',
          'sourceShow',
          'sourceItemSetList',
          'sourceItemSet',
          'sourceRow',
        ],
      );
    }

    if (itemToDuplicate.get('labels')) {
      rowData.labels = itemToDuplicate.get('labels');
    }

    if (!item.startOn) item.startOn = 0;
    if (!item.durationInSeconds) item.durationInSeconds = 0;
    if (!item.presetDurationInSeconds) item.presetDurationInSeconds = 0;

    return rowData;
  },

  /**
   * Helper for checking if item row is locked and throwing an alert
   *
   * If item is locked then throw an alert and return true.
   *
   * @returns {Boolean}
   */
  getItemRowLockStatusWithAlert(rowIndex) {
    if (this.getItemRowLockStatus(rowIndex)) {
      AlertActions.show(Messages.lockedRowModal, UIRegions.Cuesheet);
      return true;
    }
    return false;
  },

  /**
   * Helper for checking if current editing cell in a locked item row can be edited and throwing an alert
   *
   * If cell can't be edited when a row is locked then throw an alert and return.
   * @param rowIndex - num
   * @param cellSpec - Immutable Map
   * @returns {Boolean}
   */
  getEditableCellStatusInLockedRowWithAlert(rowIndex, cellSpec) {
    /** We should still be able to edit a note cell even when the row is locked */
    const isNoteCell = cellSpec.get('type') === EditableCellTypes.ItemNote;

    /** Check that the row is locked */
    const isLockedRow = this.getItemRowLockStatus(rowIndex);

    if (!isNoteCell && isLockedRow) {
      AlertActions.show(Messages.lockedRowModal, UIRegions.Cuesheet);
      return true;
    }
    return false;
  },

  /**
   * Helper for checking if item row is locked
   *
   * @returns {Boolean}
   */
  getItemRowLockStatus(rowIndex) {
    const path = this.getInState(['rows', rowIndex, 'path']);
    return this.getInState(['rowMap'].concat(path, 'locked'));
  },

  /**
   * Helper for checking if item row is a global element
   *
   * @returns {Boolean}
   */
  getItemRowGlobalElementStatus(rowIndex) {
    const path = this.getInState(['rows', rowIndex, 'path']);
    return !!this.getInState(['rowMap'].concat(path, 'globalElement'));
  },

  /**
   * If rowIndex is undefined then get the rowIndex from the row with the last
   * selected cell.
   *
   * Why do we need this?
   * We use this for things like hot key functionality.
   */
  _validRowIndexOrGetSelectedCellRowIndex(rowIndex) {
    return typeof rowIndex === 'number'
      ? rowIndex
      : this.getLastSelectedCellRowIndex();
  },

  /**
   * Get a list of row indices for selected cell range
   *
   * @param rowIndex
   * @returns {*}
   * @private
   */
  _getSelectedCellRowIndices(rowIndex) {
    if (rowIndex) return this._validRowIndexOrGetSelectedCellRowIndex();

    const itemSetId = this.findFirstItemSetId();
    const rowIndices = this.getInState('selectedCells')
      .map((selectedCell, key) => {
        const [rowId] = key.split('_');
        return this.findRowPosition(itemSetId, rowId);
      })
      .toArray();

    return rowIndices.length
      ? rowIndices
      : [this.getLastSelectedCellRowIndex()];
  },

  _getSelectedRowIds(state) {
    state = state || this.getState();

    return state
      .get('rows')
      .filter((row) => row.get('rowSelected'))
      .map((rowSpec) => rowSpec.get('path')[2]);
  },

  /**
   * Get an item w/ cells that aren't associated w/ specific columnIds
   */
  _getPastableItemData(rowIndex) {
    const itemData = this._getDuplicatedItemData(rowIndex);

    itemData.item.orderedCells = this.getInState('fullColumnOrder').map(
      (colId) => {
        return itemData.item.cells[colId];
      },
    );

    delete itemData.item.cells;

    return itemData;
  },

  /**
   * Add an item row
   *
   * @param itemSetId required
   * @param afterRowId required
   * @param items optional
   */
  _addItemRows(itemSetId, afterRowId, items) {
    let next = this.getState();

    /**
     * Used inside of global element manager. When filters are applied the new
     * rows should be automatically created with the tag or category that is
     * active in the filter.
     */
    if (next.get('labelFilter')) {
      items = items.map((item) => {
        if (next.get('labelFilter')) item.labels = next.get('labelFilter');
        return item;
      });
    }

    this.rowObservables.addItemRow.next({
      items,
      afterRowId,
      globalElement: isGlobalElementManager(),
    });
  },

  onAddGlobalElementItemRowBelow(
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    afterRowId,
    globalElement,
    transientRowData,
  ) {
    this._addGlobalElementItemRowsBelow(
      eventId,
      showId,
      itemSetListId,
      itemSetId,
      afterRowId,
      [
        {
          item: { startOn: 0 },
          globalElement,
          transientRowData: transientRowData ? transientRowData.toJS() : null,
        },
      ],
    );
  },

  _addGlobalElementItemRowsBelow(
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    afterRowId,
    items,
  ) {
    const state = this.getState();
    const cleanedItems = [...items].map(({ item, globalElement }) => {
      if (typeof item.number === 'undefined') item.number = '0';
      return {
        item,
        globalElement,
      };
    });

    CuesheetRowActions.addGlobalElementItemRowsTransient(
      itemSetId,
      afterRowId,
      items,
    );

    CuesheetRowActions.addGlobalElementItemRowsPersistent(
      state.get('teamId'),
      eventId,
      showId,
      itemSetListId,
      itemSetId,
      afterRowId,
      cleanedItems,
    );
  },

  onAddGlobalElementItemRowsPersistentCompleted(result) {
    let { itemSetId, afterRowId, rows } = result;
    let path = this.getState().getIn(['rows', 0, 'path']);

    //If itemSetId from the result doens't match the current itemSetId that is
    //in the store state then that means the global element was added to a
    //different show.
    if (itemSetId !== path[0]) return;

    this.addItemRowsTransient(itemSetId, afterRowId, rows);

    this._refreshGlobalElementRowAssociations();
  },

  onCreateGlobalElementFromShowRowCompleted() {
    this._refreshGlobalElementRowAssociations();
  },

  _refreshGlobalElementRowAssociations() {
    CuesheetRowActions.refreshGlobalElementRowAssociations(
      this.getInState('teamId'),
      this.getInState('eventId'),
      this.getInState('showId'),
    );
  },

  onRefreshGlobalElementRowAssociationsCompleted(globalElementShowItems) {
    let globalElementRowIdLookup = {};

    globalElementShowItems.forEach((ge) => {
      globalElementRowIdLookup[ge.row] = ge;
    });

    let next = this.getState().withMutations((st) => {
      st.get('rows').forEach((row) => {
        let path = row.get('path');
        let rowId = path[2];

        let itemPath = ['rowMap'].concat(path);

        st = st.updateIn(itemPath, (item) => {
          return item.set(
            'globalElement',
            globalElementRowIdLookup[rowId]
              ? fromJS(globalElementRowIdLookup[rowId])
              : false,
          );
        });
      });

      return st;
    });

    next = next.set('globalElementShowItems', globalElementShowItems);

    next = this.recalculateTimeFields(next);

    this.setStateAndTrigger(next);
  },

  onRetrieveLatestGlobalElementVersion(teamId, eventId, showId, rowIndex) {
    const state = this.getState();

    const rowSpec = state.getIn(['rows', rowIndex]),
      rowPath = rowSpec.get('path'),
      item = state.getIn(['rowMap'].concat(rowPath));

    CuesheetRowActions.retrieveLatestGlobalElementVersionPersistent(
      teamId,
      eventId,
      showId,
      item.get('globalElement'),
    );
  },

  onRetrieveLatestGlobalElementVersionPersistentCompleted(row) {
    this._retrieveLatestGlobalElementVersionTransient(row);
    ToastActions.saved();
  },

  _retrieveLatestGlobalElementVersionTransient(row) {
    let next = this.getState();

    const globalElementShowItems = next.get('globalElementShowItems'),
      itemSetId = next.get('rowMap').keySeq().first(),
      rowIndex = this.findRowPosition(itemSetId, row._id);

    let globalElementRowIdLookup = {};

    globalElementShowItems.forEach((ge) => {
      globalElementRowIdLookup[ge.row] = ge;
    });

    const rowId = row._id;
    const item = row.item[0] || { cells: {} };

    item.titleView = row.titleRow;
    item.label = row.label;
    item.category = row.category;
    item.globalElement = globalElementRowIdLookup[rowId]
      ? globalElementRowIdLookup[rowId]
      : false;

    next = next
      .setIn(['rowMap', itemSetId, 'rows', rowId, 'item'], fromJS(item))
      .setIn(['rows', rowIndex, 'touch'], new Date().getTime());

    next = this.recalculateTimeFields(next);

    this.setStateAndTrigger(next);

    CuesheetRowActions.bustRowHeightCache(rowId);
  },

  onRemoveGlobalElementReference(teamId, eventId, showId, rowIndex) {
    let next = this.getState();

    const itemSetListId = next.get('itemSetListId');
    const rowSpec = next.getIn(['rows', rowIndex]),
      rowPath = rowSpec.get('path'),
      itemSetId = rowPath[0],
      rowId = rowPath[2];

    const globalElementPath = ['rowMap']
      .concat(rowPath)
      .concat(['globalElement']);

    next = next
      .deleteIn(globalElementPath)
      .setIn(['rows', rowIndex, 'touch'], new Date().getTime());

    this.setStateAndTrigger(next);

    CuesheetRowActions.removeGlobalElementReferencePersistent(
      teamId,
      eventId,
      showId,
      itemSetListId,
      itemSetId,
      rowId,
    );
  },

  // eslint-disable-next-line no-unused-vars
  onRemoveGlobalElementReferenceCompleted(result) {
    this._refreshGlobalElementRowAssociations();
  },

  onPublishSelectedShowRowChangesToGlobalElementRow(teamId, eventId, showId) {
    CuesheetRowActions.publishShowRowChangesToGlobalElementRow(
      teamId,
      eventId,
      showId,
      this.getLastSelectedCellRowIndex(),
    );
  },

  onPublishSelectedShowRowChangesEverywhere() {
    const state = this.getState();
    const rowIndex = this.getLastSelectedCellRowIndex();

    const rowSpec = state.getIn(['rows', rowIndex]),
      rowPath = rowSpec.get('path'),
      item = state.getIn(['rowMap'].concat(rowPath));

    if (!item.get('globalElement')) return;

    PublishGlobalElementsActions.populateForShowRow(item.get('globalElement'));
  },

  onRetrieveLatestGlobalElementVersionForSelectedRow(teamId, eventId, showId) {
    const state = this.getState();
    const rowIndex = this.getLastSelectedCellRowIndex();

    const rowSpec = state.getIn(['rows', rowIndex]),
      rowPath = rowSpec.get('path'),
      item = state.getIn(['rowMap'].concat(rowPath));

    if (!item.get('globalElement')) return;

    CuesheetRowActions.retrieveLatestGlobalElementVersion(
      teamId,
      eventId,
      showId,
      rowIndex,
    );
  },

  onPublishAllShowRowChangesToGlobalElementManager() {
    const state = this.getState(),
      teamId = state.get('teamId'),
      eventId = state.get('eventId'),
      showId = state.get('showId'),
      itemSetListId = state.get('itemSetListId'),
      itemSetId = this.findFirstItemSetId();

    const rowIds = state.get('globalElementShowItems').map((item) => item.row);

    CuesheetRowActions.publishAllShowRowChangesToGlobalElementManagerPersistent(
      teamId,
      eventId,
      showId,
      itemSetListId,
      itemSetId,
      rowIds,
    );
  },

  onPublishAllShowRowChangesToGlobalElementManagerPersistentCompleted() {
    ToastActions.saved();
  },

  onPublishShowRowChangesToGlobalElementRow(teamId, eventId, showId, rowIndex) {
    const state = this.getState();

    const itemSetListId = state.get('itemSetListId');
    const rowSpec = state.getIn(['rows', rowIndex]),
      rowPath = rowSpec.get('path'),
      itemSetId = rowPath[0],
      rowId = rowPath[2],
      item = state.getIn(['rowMap'].concat(rowPath));

    if (!item.get('globalElement')) return;

    CuesheetRowActions.publishShowRowChangesToGlobalElementRowPersistent(
      teamId,
      eventId,
      showId,
      itemSetListId,
      itemSetId,
      rowId,
    );
  },

  // eslint-disable-next-line no-unused-vars
  onPublishShowRowChangesToGlobalElementRowPersistentCompleted(result) {
    ToastActions.saved();
  },

  _addItemRowsAbove(rowIndex, items) {
    let afterRowId;
    const actualRowIndex = rowIndex ? rowIndex - 1 : 0;

    const path = this.getInState(['rows', actualRowIndex, 'path']);
    const itemSetId = path[0];
    if (rowIndex > 0) afterRowId = path[2];

    this._addItemRows(itemSetId, afterRowId, items);
  },

  _addItemRowsBelow(rowIndex, items) {
    const next = this.getState();

    /**
     * Use unfilteredRows when in global element manager and no rows match the
     * filter applied to category or tags
     */
    const rowField = this._getRowFieldForAppliedFilter();

    const path = next.getIn([rowField, rowIndex, 'path']);
    const itemSetId = path[0],
      afterRowId = path[2];

    this._addItemRows(itemSetId, afterRowId, items);
  },

  _reorderCutItemRows(rowIndex) {
    const rows = this.getInState('rows');
    const pastableRowIds = this.getInState('pastableRowIds');

    const pastedRowIds = pastableRowIds
      .map((row) => row.get('rowId'))
      .filter(Boolean)
      .toArray();

    if (!pastedRowIds.length) return;

    const rowsWithoutCuts = rows.reduce((rowArray, row, index) => {
      const rowPath = row.get('path');
      const rowId = rowPath ? rowPath[2] : null;
      let isPastableRow = false;
      pastableRowIds.forEach((pastableRow) => {
        if (pastableRow.get('rowId') === rowId) isPastableRow = true;
      });
      if (!isPastableRow) {
        rowArray = rowArray.push(row.set('originalIndex', index));
      }
      return rowArray;
    }, List());

    let incrementPasteIndex = false;

    let pasteIndex = rowsWithoutCuts.findIndex((row) => {
      if (row.get('originalIndex') === rowIndex) incrementPasteIndex = true;
      return row.get('originalIndex') >= rowIndex;
    });

    if (incrementPasteIndex) pasteIndex += 1;

    //Row paths are plain arrays inside the row spec Maps, so getIn cannot
    //traverse into them — read the path and index it instead
    const rowIdAt = (index) => {
      const rowPath = rowsWithoutCuts.getIn([index, 'path']);
      return rowPath ? rowPath[2] : undefined;
    };

    //The cut rows go after the row directly above the paste point, or to
    //the top when there is none. A paste point past every remaining row
    //(pasteIndex of -1) means they go after the last row.
    let afterRowId;
    if (pasteIndex === -1) {
      if (rowsWithoutCuts.size) {
        afterRowId = rowIdAt(rowsWithoutCuts.size - 1);
      }
    } else if (pasteIndex > 0) {
      afterRowId = rowIdAt(pasteIndex - 1);
    }

    /**
     * Route the paste through the same observable as move gestures: the
     * transient subscriber arranges the rows locally, and the buffered
     * flush persists this delta in gesture order relative to any moves
     * still waiting in the buffer. Dispatching it directly would let the
     * paste reach the API before moves the user made just before it,
     * making the server apply the deltas in a different order than the
     * user saw locally.
     */
    this.rowObservables.moveRowsDebounce.next({
      rowIds: pastedRowIds,
      afterRowId,
    });
  },

  /**
   * Persist buffered move gestures as moveRows deltas (rowIds + afterRowId).
   *
   * We intentionally do NOT persist a full rowOrder snapshot here: a snapshot
   * computed from this client's (possibly stale) view of the order silently
   * reverts row moves other users made concurrently. The moveRows endpoint
   * re-applies each delta to the order currently in the DB, so concurrent
   * moves compose instead of clobbering each other.
   */
  _persistMoveDeltas(moves) {
    const state = this.getState();
    const itemSetId = this.findFirstItemSetId();

    const deltas = [];

    moves.forEach(({ rowIds, afterRowId }) => {
      const ids = List.isList(rowIds) ? rowIds.toArray() : [].concat(rowIds);
      const previous = deltas[deltas.length - 1];

      //Consecutive moves of the same selection (e.g. a repeated "move down")
      //collapse into a single move to the final position
      if (previous && _.isEqual(previous.rowIds, ids)) {
        previous.afterRowId = afterRowId;
      } else {
        deltas.push({ rowIds: ids, afterRowId });
      }
    });

    deltas.forEach(({ rowIds, afterRowId }) => {
      this._persistMoveRows(state, itemSetId, rowIds, afterRowId);
    });
  },

  _persistMoveRows(state, itemSetId, rowIds, afterRowId) {
    debug('Persisting row move', rowIds, afterRowId);

    const context = {
      showId: state.get('showId'),
      itemSetId,
      //Stamped only when the request actually leaves (in the chain link
      //below), not now. A move can sit queued behind a slow request for a
      //while; leaving dispatchedAt null until dispatch keeps the
      //unsettled-request TTL measuring time on the wire, so a still-queued
      //move can't age out of pending protection before it is even sent.
      dispatchedAt: null,
    };

    this._inFlightMoveContexts.push(context);

    /**
     * Notification only — the GEM stores sync their local order copies from
     * this action. It fires eagerly (not behind the request chain) so the GEM
     * optimistic order matches the cuesheet's, in gesture order. The request
     * itself is made below so its settlement can be tied back to `context` by
     * identity (Reflux completed/failed callbacks carry no request identity).
     * No read-only gate is needed here: every gesture that can feed the move
     * buffer is itself a read-only gated action.
     */
    CuesheetRowActions.rowsMovedLocally(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      itemSetId,
      rowIds,
      afterRowId,
    );

    /**
     * Chain the request so only one moveRows is ever in flight. The endpoint
     * applies each delta as a non-atomic read-modify-write of rowOrder, so two
     * overlapping requests would both read the same starting order and the
     * second write would clobber the first — silently dropping a move. Serial
     * dispatch makes the server apply the deltas one at a time, in the gesture
     * order the user performed locally. dispatchedAt is stamped when the
     * request actually leaves, not when it queues, so the unsettled-request
     * TTL measures time on the wire.
     *
     * The chain link is bounded by PENDING_MOVE_TTL_MS: a request that never
     * settles (dead socket with no transport timeout) would otherwise stall
     * every queued move behind it forever. After the TTL we release the chain
     * so later gestures still reach the server. The original request is left
     * to settle on its own — by then its context has aged out of
     * _inFlightMoveContexts, so a late settlement is a no-op by identity. This
     * does mean a request slower than the TTL can briefly overlap the next
     * one, but that is far rarer, and self-corrects, where a permanent stall
     * would not.
     */
    this._moveRowsRequestChain = this._moveRowsRequestChain.then(() => {
      context.dispatchedAt = new Date().getTime();

      const request = ItemSetData.moveRows(
        state.get('teamId'),
        state.get('eventId'),
        state.get('showId'),
        state.get('itemSetListId'),
        itemSetId,
        rowIds,
        afterRowId,
      ).then(
        (rowOrder) => this._onMoveRowsSettled(context, rowOrder),
        (err) => this._onMoveRowsSettled(context, null, err),
      );

      let releaseChain;
      const chainTimeout = new Promise((resolve) => {
        releaseChain = setTimeout(resolve, PENDING_MOVE_TTL_MS);
      });

      //Settle the chain link on both fulfilment and rejection: if a settle
      //callback (e.g. _updateRowOrder) ever throws, the link must not reject
      //_moveRowsRequestChain — a rejected chain would skip every later move's
      //.then() callback, silently killing all row-move persistence for the
      //session. Clearing the timer in both branches also avoids leaking it.
      const release = () => clearTimeout(releaseChain);
      return Promise.race([request, chainTimeout]).then(release, release);
    });

    return this._moveRowsRequestChain;
  },

  /**
   * Whether this client has row moves that the API has not confirmed yet.
   * While that's the case, the local optimistic order is newer than any row
   * order arriving from outside (realtime events, our own earlier responses).
   *
   * Pass `itemSetId` to ask only about moves targeting that itemSet: a move
   * pending for itemSet A must not suppress realtime orders for an unrelated
   * itemSet B (e.g. an element set open in the Global Element Modal). Omit it
   * to ask about any pending move.
   */
  _hasPendingRowMoves(itemSetId) {
    if (!this._hasBufferedRowMoves && !this._inFlightMoveContexts.length)
      return false;

    const now = new Date().getTime();

    /**
     * Self-heal the in-flight phase only: drop tracking entries whose request
     * has gone unsettled for a long time (e.g. hung on a dead connection) so
     * remote row order updates aren't held back forever. A late settlement of
     * a dropped entry is harmless — it no longer matches anything by identity.
     *
     * Entries still queued behind an earlier request (dispatchedAt === null)
     * are never dropped here: the TTL measures time on the wire, and a queued
     * move hasn't been sent yet, so its optimistic order must keep suppressing
     * remote orders until it is dispatched and either settles or times out.
     *
     * The buffered phase is intentionally NOT self-healed: _hasBufferedRowMoves
     * is cleared only by the debounced flush, which is also what persists the
     * deltas. Clearing it on a timer would end protection while the Rx buffer
     * still holds un-persisted moves, letting a realtime order overwrite the
     * optimistic order before they reach the API.
     */
    this._inFlightMoveContexts = this._inFlightMoveContexts.filter(
      (context) =>
        context.dispatchedAt === null ||
        now - context.dispatchedAt <= PENDING_MOVE_TTL_MS,
    );

    if (itemSetId === undefined) {
      return this._hasBufferedRowMoves || this._inFlightMoveContexts.length > 0;
    }

    //Buffered moves always target the cuesheet's own itemSet (the flush goes
    //through findFirstItemSetId); in-flight contexts carry their itemSetId.
    if (this._hasBufferedRowMoves && this.findFirstItemSetId() === itemSetId)
      return true;

    return this._inFlightMoveContexts.some(
      (context) => context.itemSetId === itemSetId,
    );
  },

  /*
   * Settle a moveRows request: release its tracking entry, then re-sync to
   * the order the server computed and recalculate derived fields
   */
  _onMoveRowsSettled(context, rowOrder, err) {
    const contextIndex = this._inFlightMoveContexts.indexOf(context);
    if (contextIndex !== -1) this._inFlightMoveContexts.splice(contextIndex, 1);

    if (err) {
      debug('Failed to persist row move', err);

      ToastActions.failure(
        'Row move failed to save. Attempting to sync row order from db',
        4000,
      );
    }

    /**
     * While further local moves are buffered or in flight, the local
     * optimistic order is newer than this response — wait for the burst's
     * last settlement before re-syncing and recalculating. Whatever that final
     * settlement is, it re-syncs to DB truth below: a success applies its
     * authoritative response, a failure refetches.
     */
    if (this._hasPendingRowMoves()) return;

    /**
     * Apply the authoritative order from the response (it includes moves
     * other clients made concurrently, and reflects that any failed move in
     * this burst never persisted). Skip it when this request's tracking entry
     * already expired — a response that stale may predate remote orders applied
     * in the meantime. _updateRowOrder already recalculates time fields and
     * triggers, so the derived recalculation below only runs when there is no
     * order to re-sync to.
     */
    if (contextIndex !== -1 && Array.isArray(rowOrder) && rowOrder.length) {
      //Share the authoritative merged order with the GEM stores. They mirror
      //this itemSet but only saw the optimistic delta (rowsMovedLocally) and
      //never receive this HTTP response, so without this they keep a stale
      //order when the response merged in a concurrent remote move (or when a
      //realtime order was deferred while our move was pending).
      CuesheetRowActions.rowOrderResynced(
        context.showId,
        context.itemSetId,
        rowOrder,
      );

      return this._updateRowOrder(context.showId, context.itemSetId, rowOrder);
    }

    /**
     * The final settlement failed with no authoritative order to apply — refetch
     * the DB order and revert to it (rather than a local snapshot, which would
     * clobber concurrent remote moves), so the failed move is never left stale.
     */
    if (err) {
      return this._refetchAndResyncRowOrder(context);
    }

    let next = this.getState();

    next = CuesheetDataHelper.getStateWithDerivedCalculations(
      next,
      this.getInState(['show', 'autoTimeCalculation']),
      this.getInState(['show', 'startOn']),
    );

    next = CuesheetDataHelper.getStateWithTotalDurations(next);

    if (this.getInState(['show', 'autoCountCues'])) {
      next = this.touchAllRows(next);
    }

    this.setStateAndTrigger(next);
  },

  /*
   * Refetch the authoritative row order from the DB and apply it. Used to revert
   * the optimistic order after a moveRows request fails, so rows return to their
   * correct position. Mirrors the success path: it also shares the order with the
   * GEM stores (rowOrderResynced), which only saw the optimistic delta.
   */
  _refetchAndResyncRowOrder(context) {
    return ItemSetListData.getRowOrder(
      this.getInState('teamId'),
      this.getInState('eventId'),
      context.showId,
      this.getInState('itemSetListId'),
    ).then(
      (rowOrder) => {
        if (!Array.isArray(rowOrder) || !rowOrder.length) return;

        //A move made while the fetch was in flight makes the local order newer
        //than this response again; its own settlement will re-sync, so don't
        //clobber it here.
        if (this._hasPendingRowMoves(context.itemSetId)) return;

        CuesheetRowActions.rowOrderResynced(
          context.showId,
          context.itemSetId,
          rowOrder,
        );

        this._updateRowOrder(context.showId, context.itemSetId, rowOrder);
      },
      (fetchErr) => {
        //The refetch that would revert the optimistic order failed too, so the
        //stale order is still on screen. Surface it rather than swallowing — the
        //local order can't be trusted until a reload re-syncs with the DB.
        console.error(
          'Failed to refetch row order after failed move',
          fetchErr,
        );
        ToastActions.failure(
          'Could not restore the saved row order. Please reload.',
          6000,
        );
      },
    );
  },

  /**
   * Swap the position of two rows...useful for 'move up' and 'move down' scenarios
   *
   */
  _swapRows(fromIndex, toIndex) {
    const next = this.getState();

    /**
     * Express the swap as the cursor row (the one at `fromIndex`) moving to
     * `toIndex`, keeping that row as the subject of the delta. A run of moves
     * in the same direction then dispatches the same `rowIds` on every step,
     * so _persistMoveDeltas collapses the whole burst into a single moveRows
     * request.
     *
     * The previous approach always moved the *upper* row of the pair down,
     * which made an up-run swap a different neighbor on each step — those
     * deltas never collapsed, so every step hit the API.
     */
    const rowId = this.findRowId(fromIndex, next);

    let afterRowId;
    if (toIndex > fromIndex) {
      //Moving down: land after whatever currently sits at the destination
      afterRowId = this.findRowId(toIndex, next);
    } else if (toIndex > 0) {
      //Moving up: land after the row directly above the destination
      afterRowId = this.findRowId(toIndex - 1, next);
    }
    //toIndex === 0 leaves afterRowId undefined → move to the top

    this.rowObservables.moveRowsDebounce.next({
      rowIds: [rowId],
      afterRowId,
    });
  },

  onMoveFromTo(sourceRowId, targetRowIndex, params) {
    const ids = params || this.getState(),
      teamId = ids.get('teamId'),
      eventId = ids.get('eventId'),
      showId = ids.get('showId'),
      itemSetListId = ids.get('itemSetListId'),
      itemSetId = ids.get('itemSetId');

    let next = this.moveFromToTransient(
      showId,
      itemSetId,
      sourceRowId,
      targetRowIndex,
    );

    next = this.updateUnfilteredRows(next.get('rows'), next);

    CuesheetRowActions.moveFromToPersistent(
      teamId,
      eventId,
      showId,
      itemSetListId,
      itemSetId,
      sourceRowId,
      targetRowIndex,
    );

    next = this.recalculateTimeFields(next);

    this.setStateAndTrigger(next);
  },

  moveFromToTransient(showId, itemSetId, sourceRowId, targetRowIndex) {
    let state = this.getState();

    //If showId don't match then this change was made for a different than the
    //one that is currently loaded into grid view.
    //Potentially done through drag and drop of global element insert modal.
    if (showId !== this.getInState('showId')) return state;

    return state.withMutations((st) => {
      let originalRowIndex = this.findRowPosition(itemSetId, sourceRowId);

      let rowSpec = this.getInState(['rows', originalRowIndex]);

      st.update('rows', (rows) => rows.delete(originalRowIndex));
      st.update('rows', (rows) => rows.splice(targetRowIndex, 0, rowSpec));

      return st;
    });
  },

  moveRowsTransient(itemSetId, rowIds, afterRowId) {
    const state = this.getState();

    /**
     * Stale anchor: a defined afterRowId that's no longer in the order means we
     * can't place this move, so skip it and wait for the authoritative resync
     * — exactly as applyRowMoveDelta does in the GEM stores, so the cuesheet
     * and GEM stay consistent. (An undefined afterRowId is the "move to the
     * top" signal and must still apply.) Guarding up front rather than per row
     * avoids deleting rows we then can't reinsert.
     */
    if (
      afterRowId !== undefined &&
      this.findRowPosition(itemSetId, afterRowId, state) === -1
    ) {
      return state;
    }

    //Reverse a copy: Array.prototype.reverse mutates in place and the same
    //event object is read later by the buffered persistence flush
    const reversedRowIds = (
      List.isList(rowIds) ? rowIds : List(rowIds)
    ).reverse();

    return state.withMutations((st) => {
      reversedRowIds.forEach((rowId) => {
        /**
         * Look each row up in the mutating state: earlier iterations shift
         * positions, so indexes from the original state can point at the
         * wrong row (e.g. moving several rows to a spot above them). This
         * mirrors how the API applies the delta to the current order.
         */
        const originalRowIndex = this.findRowPosition(itemSetId, rowId, st);

        //Skip rows that no longer exist (e.g. deleted between cut and paste)
        if (originalRowIndex === -1) return;

        const rowSpec = st.getIn(['rows', originalRowIndex]);

        st.update('rows', (rows) => rows.delete(originalRowIndex));

        const afterRowPosition = this.findRowPosition(
          itemSetId,
          afterRowId,
          st,
        );

        st.update('rows', (rows) =>
          rows.splice(afterRowPosition + 1, 0, rowSpec),
        );
      });
    });
  },

  /**
   * Delete a row in local state
   */
  deleteRowsTransient(itemSetId, rowIds) {
    let next = this.getState();

    rowIds.forEach((rowId) => {
      const rowIndex = this.findRowPosition(itemSetId, rowId, next);

      /**
       * Changes in support of #5386
       */
      let unfilteredRows = next.get('unfilteredRows');

      if (unfilteredRows) {
        const unfilteredIndex = this.findRowPositionInListOfRows(
          itemSetId,
          rowId,
          unfilteredRows,
        );

        if (unfilteredRows !== -1) {
          next = next.set(
            'unfilteredRows',
            unfilteredRows.delete(unfilteredIndex),
          );
        }
      }

      //If the row index is -1, it means perhaps the item removed isn't in the user's view, and it's in 'unfilteredRows'
      if (rowIndex === -1) return;

      /**
       * When we delete a transient row, if someone with a different session was editing,
       * we need to clear their transient value, otherwise when they open a new cell editor,
       * this transient state will be visible.
       */
      if (next.getIn(['rows', rowIndex, 'editing'])) {
        CuesheetTransientCellActions.clearTransientCellValue();
      }
      next = next.deleteIn(['rows', rowIndex]);
    });

    next = this.removeCellSelectionsByRowIds(rowIds, next);
    next = this.recalculateCellSelectionRange(next);

    this.recalculateTimeFieldsAndTrigger(next);
  },

  onFilterGlobalElementManagerRowsOnLoad(field, values) {
    this.setInState('filterRowsOnLoad', { field, values });
  },

  onClearGlobalElementManagerRowsFilter(field) {
    if (!this.getInState('unfilteredRows')) return;
    this._filterRows(field, '');
  },

  onFilterGlobalElementManagerRows(field, values) {
    this._filterRows(field, values);
  },

  _justFilterRows(next) {
    /**
     * Step 1: Save original row snapshot
     *
     * We always want to perform our filter on the entire row set.
     * Since rows get's filtered down after the first filter, we
     * save a snapshot of the orginal rows to apply future filters to.
     */
    if (!next.get('unfilteredRows'))
      next = next.set('unfilteredRows', next.get('rows'));

    let filteredRows = next.get('unfilteredRows');

    /**
     * Step 2: Filter rows
     */
    const labelFilter = next.get('labelFilter') || '';

    filteredRows = filteredRows.filter((row) => {
      if (!labelFilter || labelFilter.length < 1) return true;

      const item = next.get('rowMap').getIn(row.get('path'));

      let allLabelFiltersMatched = true;

      labelFilter.forEach((labelFilterId) => {
        const itemLabels = item.get('labels');

        if (
          !itemLabels ||
          itemLabels.length < 1 ||
          itemLabels.indexOf(labelFilterId) === -1
        ) {
          allLabelFiltersMatched = false;
        }
      });

      return allLabelFiltersMatched;
    });

    const searchFilter = next.get('searchFilter');

    if (searchFilter) {
      filteredRows = filteredRows.filter(function searchQueryRowsFilter(row) {
        const item = next.get('rowMap').getIn(row.get('path'));

        const regex = new RegExp(searchFilter, 'gi');
        return regex.test(item.get('title'));
      });
    }

    next = next.set('rows', filteredRows);

    return next;
  },

  //this preserves the touch, track, and trackedUsers fields because those fields aren't
  //updated on the unfiltered rows
  updateUnfilteredRowsWithRowFields(rows, filteredRows) {
    filteredRows = filteredRows.update((fRows) =>
      fRows.map((row) =>
        row.delete('tracked').delete('touch').delete('trackedUsers'),
      ),
    );

    rows.forEach((row) => {
      let rowId = row.get('path')[2];
      let touched = row.has('touch') ? row.get('touch') : null;
      let tracked = row.has('tracked');
      let trackedUsers = row.has('trackedUsers')
        ? row.get('trackedUsers')
        : null;

      let rowIndex = filteredRows.findKey(
        (row) => row.get('path')[2] === rowId,
      );

      if (rowIndex) {
        if (touched)
          filteredRows = filteredRows.setIn([rowIndex, 'touch'], touched);
        if (tracked)
          filteredRows = filteredRows.setIn([rowIndex, 'tracked'], true);
        if (trackedUsers)
          filteredRows = filteredRows.setIn(
            [rowIndex, 'trackedUsers'],
            trackedUsers,
          );
      }
    });

    return filteredRows;
  },

  _filterRows(field, values) {
    let next = this.getState().set(`${field}Filter`, values);

    next = this._justFilterRows(next);

    next = next.delete('filterRowsOnLoad');

    next = this.deselectAllCells(next);

    this.setStateAndTrigger(next);

    CuesheetRowActions.clearRowSelection();
  },

  updateUnfilteredRows(rows, state) {
    let next = state || this.getState();

    next = next.set('unfilteredRows', rows);

    if (state) return next;
    this.setState(next);
  },

  _getRowFieldForAppliedFilter() {
    const next = this.getState();

    /**
     * Use unfilteredRows when in global element manager and no rows match the
     * filter applied to category or tags
     */
    return next.get('rows').size < 1 &&
      next.get('unfilteredRows') &&
      next.get('unfilteredRows').size > 0
      ? 'unfilteredRows'
      : 'rows';
  },
};
